/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import React, { useEffect, useRef, useState } from "react";
import { Box, Fab } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { Add01Icon, MinusSignIcon } from "@hugeicons-pro/core-stroke-standard";
 
// Props: we receive scaleValue and handlers from parent
interface BottomRightBoxProps {
  scaleValue: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
}
 
export const BottomRightBox: React.FC<BottomRightBoxProps> = ({
  scaleValue,
  onZoomIn,
  onZoomOut,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });
 
  // Initial position: bottom-right, 10px from edges
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
 
    const place = (): void => {
      void el.offsetHeight;
      const rect = el.getBoundingClientRect();
      const vpW = window.innerWidth;
      const vpH = window.innerHeight;
 
      setPos({
        x: vpW - rect.width - 10,
        y: vpH - rect.height - 10,
      });
    };
 
    const raf = requestAnimationFrame(() => requestAnimationFrame(place));
    const timeout = setTimeout(place, 200);
    const onResize = (): void => place();
    window.addEventListener("resize", onResize);
 
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.removeEventListener("resize", onResize);
    };
  }, []);
 
  // Drag handlers
  const startDrag = (e: React.MouseEvent | React.TouchEvent): void => {
    setIsDragging(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    startPos.current = { x: clientX - pos.x, y: clientY - pos.y };
  };

  const onDrag = (e: MouseEvent | TouchEvent): void => {
    if (!isDragging) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
 
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const el = boxRef.current!;
    const rect = el.getBoundingClientRect();
 
    let newX = clientX - startPos.current.x;
    let newY = clientY - startPos.current.y;
 
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
 
    newX = Math.max(0, Math.min(newX, vpW - rect.width));
    newY = Math.max(0, Math.min(newY, vpH - rect.height));
 
    setPos({ x: newX, y: newY });
  };
 
  const stopDrag = () : void=> setIsDragging(false);
 
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", onDrag);
      document.addEventListener("mouseup", stopDrag);
      document.addEventListener("touchmove", onDrag, { passive: false });
      document.addEventListener("touchend", stopDrag);
    }
    return () => {
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", stopDrag);
      document.removeEventListener("touchmove", onDrag);
      document.removeEventListener("touchend", stopDrag);
    };
  }, [isDragging]);
 
  // Use props directly — DO NOT redefine handlers here
  return (
<Box
      ref={boxRef}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#fff",
        border: "1px solid #e0e0e0",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        zIndex: 9999,
      }}
>
      {/* Zoom In */}
<Fab
        size="small"
        onClick={onZoomIn}  // Use prop
        disabled={scaleValue >= 24}
        sx={{
          width: "44px",
          height: "44px",
          borderRadius: "8px 8px 0 0",
          backgroundColor: "#fff",
          border: "none",
          borderBottom: "1px solid #e0e0e0",
          boxShadow: "none",
          color: "#333",
          minHeight: "unset",
          "&:hover": { backgroundColor: "#f8f9fa", boxShadow: "none" },
          "&:disabled": { backgroundColor: "#f5f5f5", color: "#bbb" },
        }}
>
<HugeiconsIcon icon={Add01Icon} size={20} color="currentcolor" strokeWidth={2} />
</Fab>
 
      {/* Scale Display */}
<Box
        sx={{
          width: "44px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #e0e0e0",
          borderBottom: "1px solid #e0e0e0",
          fontSize: "13px",
          fontWeight: 600,
          color: "#333",
          userSelect: "none",
        }}
>
        {scaleValue}h
</Box>
 
      {/* Zoom Out */}
<Fab
        size="small"
        onClick={onZoomOut}  // Use prop
        disabled={scaleValue <= 1}
        sx={{
          width: "44px",
          height: "44px",
          borderRadius: "0 0 8px 8px",
          backgroundColor: "#fff",
          border: "none",
          borderTop: "1px solid #e0e0e0",
          boxShadow: "none",
          color: "#333",
          minHeight: "unset",
          "&:hover": { backgroundColor: "#f8f9fa", boxShadow: "none" },
          "&:disabled": { backgroundColor: "#f5f5f5", color: "#bbb" },
        }}
>
<HugeiconsIcon icon={MinusSignIcon} size={20} color="currentcolor" strokeWidth={2} />
</Fab>
</Box>
  );
};